import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './VotePage.css';

function VotePage() {
  const { code } = useParams();
  const [name, setName] = useState('');
  const [selectedVote, setSelectedVote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [previousVote, setPreviousVote] = useState(null);
  const [error, setError] = useState('');
  const [sessionExists, setSessionExists] = useState(true);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [config, setConfig] = useState(null);
  const [confidenceLevels, setConfidenceLevels] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [autoCloseWarning, setAutoCloseWarning] = useState(null);

  useEffect(() => {
    // Load configuration
    const loadConfig = async () => {
      try {
        const response = await fetch('/config.json');
        const configData = await response.json();
        setConfig(configData);
        
        // Convert config to levels array
        const levels = [5, 4, 3, 2, 1].map(value => ({
          value,
          label: configData.vote_levels[value].label,
          description: configData.vote_levels[value].description,
          image: configData.vote_levels[value].image
        }));
        setConfidenceLevels(levels);
      } catch (err) {
        console.error('Failed to load config:', err);
        setConfidenceLevels([]);
      }
    };
    
    loadConfig();
  }, []);

  useEffect(() => {
    // Check if session exists and restore previous vote
    const checkSession = async () => {
      try {
        const sessionData = await api.getSession(code);
        setSessionExists(true);
        setIsAnonymous(sessionData.anonymous || false);
        setSessionClosed(sessionData.closed || false);

        // Set up auto-close warning timer
        if (sessionData.auto_close_at && !sessionData.closed) {
          const closeTime = new Date(sessionData.auto_close_at).getTime();
          const checkWarning = () => {
            const remaining = closeTime - Date.now();
            if (remaining <= 0) {
              setAutoCloseWarning(null);
            } else if (remaining <= 30 * 60 * 1000) {
              const mins = Math.ceil(remaining / 60000);
              setAutoCloseWarning(`Session will auto-close in ${mins} minute${mins !== 1 ? 's' : ''}`);
            }
          };
          checkWarning();
          const warningInterval = setInterval(checkWarning, 30000);
          // Store for cleanup
          window._voteWarningInterval = warningInterval;
        }

        // Check for previous vote from this browser
        const savedName = localStorage.getItem(`vote_name_${code}`);
        if (savedName && !sessionData.anonymous) {
          setName(savedName);
          try {
            const myVote = await api.getMyVote(code, savedName);
            setPreviousVote(myVote.vote);
            setSelectedVote(myVote.vote);
            setSubmitted(true);
          } catch (e) {
            // No previous vote found — that's fine
          }
        }
      } catch (err) {
        setSessionExists(false);
        setError('Session not found');
      }
    };
    checkSession();
    return () => {
      if (window._voteWarningInterval) clearInterval(window._voteWarningInterval);
    };
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAnonymous && !name.trim()) {
      setError('name');
      return;
    }
    
    if (selectedVote === null) {
      setError('vote');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await api.submitVote(code, isAnonymous ? '' : name, selectedVote);
      // Remember name for this session (for re-voting)
      if (!isAnonymous) {
        localStorage.setItem(`vote_name_${code}`, name);
      }
      setPreviousVote(selectedVote);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit vote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeVote = () => {
    setSubmitted(false);
  };

  if (!sessionExists) {
    return (
      <div className="vote-page">
        <div className="vote-container">
          <h2>Session Not Found</h2>
          <p>The session code "{code}" doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (sessionClosed) {
    return (
      <div className="vote-page">
        <div className="vote-container">
          <div className="session-closed-message">
            <h2>⛔ Session Closed</h2>
            <p>This voting session is no longer accepting votes.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!config || confidenceLevels.length === 0) {
    return (
      <div className="vote-page">
        <div className="vote-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="vote-page">
        <div className="vote-container">
          <div className="success-message">
            <h2>✓ {previousVote !== null && previousVote !== selectedVote ? 'Vote Updated!' : 'Thank You!'}</h2>
            <p>Your vote has been recorded.</p>
            {!isAnonymous && <p className="voter-name">Voter: {name}</p>}
            <p className="vote-value">Confidence: {selectedVote}</p>
            {!sessionClosed && (
              <button className="change-vote-btn" onClick={handleChangeVote}>
                Change Vote
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vote-page">
      <div className="vote-container">
        <h1>{config.title}</h1>
        <p className="session-code">Session: {code}</p>
        
        {autoCloseWarning && (
          <div className="auto-close-warning">
            ⚠️ {autoCloseWarning}
          </div>
        )}
        <form onSubmit={handleSubmit} className="vote-form">
          {previousVote !== null && !isAnonymous && (
            <div className="revote-notice">
              ✏️ You previously voted <strong>{previousVote}</strong> — select a new level to change your vote
            </div>
          )}

          {!isAnonymous && (
            <div className="form-group">
              <label htmlFor="name">Your Name: <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => { setName(e.target.value); if (error === 'name') setError(''); }}
                placeholder="Enter your name"
                maxLength={50}
                disabled={loading || previousVote !== null}
                className={error === 'name' ? 'input-error' : ''}
              />
              {error === 'name' && <span className="field-error">Please enter your name</span>}
            </div>
          )}

          {isAnonymous && (
            <div className="anonymous-notice">
              🔒 This is an anonymous vote - your identity will not be recorded
            </div>
          )}

          <div className="form-group">
            <label>Select Your Confidence Level:</label>
            {error === 'vote' && <span className="field-error">Please select a confidence level</span>}
            <div className="confidence-levels">
              {confidenceLevels.map(level => (
                <div 
                  key={level.value} 
                  className={`confidence-level ${selectedVote === level.value ? 'selected' : ''}`}
                  onClick={() => !loading && setSelectedVote(level.value)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      !loading && setSelectedVote(level.value);
                    }
                  }}
                >
                  <img src={level.image} alt={`${level.value} fingers`} className="hand-image" />
                  <div className="level-header">
                    <strong>{level.label}</strong>
                    <p className="level-description">{level.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {error && error !== 'name' && error !== 'vote' && <p className="error-message">{error}</p>}
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Submitting...' : previousVote !== null ? 'Update Vote' : 'Submit Vote'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default VotePage;
